import { pagesConfig } from '../../shared/pages-config';
import {
  AdministrationRoles,
  DirectoryRoles,
  isAbleToSeeUIElement,
  RulesEngineRoles,
  ToolsRoles,
  UserRoles,
} from '../../utils/roles/client-roles';

import AdminPanelSettingsOutlined from '@mui/icons-material/AdminPanelSettingsOutlined';
import Event from '@mui/icons-material/Event';
import GroupOutlined from '@mui/icons-material/GroupOutlined';
import GroupsOutlined from '@mui/icons-material/GroupsOutlined';
import PendingActionsOutlined from '@mui/icons-material/PendingActionsOutlined';
import Speed from '@mui/icons-material/Speed';
import type SvgIcon from '@mui/material/SvgIcon';

type SvgIconComponent = typeof SvgIcon;

export type CommandDashboardTileKey =
  | 'activeUsers'
  | 'groupsAndAccess'
  | 'rulesEngine'
  | 'recentAdminActions'
  | 'loginActivity'
  | 'masterScheduler';

export type CommandDashboardTileAccentColor =
  | 'violet'
  | 'teal'
  | 'amber'
  | 'green'
  | 'blue'
  | 'blueDark';

/** The `useUserSettings` field holding the roles a tile's permission lives in. */
export type CommandDashboardTileRoleGroup =
  | 'administrationRoles'
  | 'directoryRoles'
  | 'rulesEngineRoles'
  | 'toolsRoles'
  | 'userRoles';

/**
 * A permission, paired with its role group because a tile's permissions are not
 * all guaranteed to come from the same one.
 */
export interface CommandDashboardTilePermission {
  roleGroup: CommandDashboardTileRoleGroup;
  permission: string;
  // Human-readable label for the "you need X access" messaging shown when this permission is
  // what's missing (the Tile Library's disabled-tile tooltip, and a placed tile's disabled
  // Refresh tooltip once permission is revoked).
  name: string;
}

export type CommandDashboardTileSizeProfile = 'standard' | 'wide';

interface CommandDashboardTileSize {
  columns: number;
  width: number;
  height: number;
}

interface CommandDashboardTileSizeProfileDefinition {
  /** Capacity consumed in a three-unit row. */
  rowUnits: 1 | 2;
  /** Widest first, so the last entry is the narrowest (and tallest) size. */
  sizes: readonly CommandDashboardTileSize[];
}

/** Columns a tile spans when it has a row to itself. */
export const FULL_WIDTH_TILE_COLUMNS = 12;

/** Columns a standard tile spans when it shares a row with one other. */
export const HALF_WIDTH_TILE_COLUMNS = 6;

/** Columns a standard tile spans when it shares a row with two others. */
export const THIRD_WIDTH_TILE_COLUMNS = 4;

/**
 * The shared grid every full-width tile body draws on: 24px of padding, then
 * two equal regions - hero left, secondary stats right - 90px apart.
 */
export const TILE_CONTENT_PADDING = 24;

export const WIDE_TILE_REGIONS_GAP = 90;

export const wideTileRegionSx = { flex: '1 1 0', minWidth: 0 } as const;

export const SECONDARY_STAT_PADDING = '12px 16px 12px 12px';

export const SECONDARY_STAT_LABEL_VALUE_GAP = 9;

export const STATS_ROW_GAP_FLOOR = 16;

/**
 * The two Figma size families. Widths document the 1622px design canvas;
 * grid columns are what preserve those proportions at other dashboard widths.
 */
export const COMMAND_DASHBOARD_TILE_SIZE_PROFILES: Record<
  CommandDashboardTileSizeProfile,
  CommandDashboardTileSizeProfileDefinition
> = {
  standard: {
    rowUnits: 1,
    sizes: [
      { columns: 12, width: 1622, height: 340 },
      { columns: 6, width: 799, height: 480 },
      { columns: 4, width: 525, height: 684 },
    ],
  },
  wide: {
    rowUnits: 2,
    sizes: [
      { columns: 12, width: 1622, height: 684 },
      { columns: 8, width: 1074, height: 684 },
    ],
  },
};

/**
 * The size a tile is being drawn at when it spans `columns`.
 *
 * A dashboard narrower than Figma's 1622px canvas draws every size
 * proportionally smaller, so a half-width tile is only 799px wide on a canvas
 * that wide. The column span, not a pixel width, is what identifies which of a
 * profile's sizes a tile is at - which is why tile bodies pick their layout
 * from this rather than from a container query.
 */
export function tileSizeForColumns(
  tile: CommandDashboardTileDefinition,
  columns: number
): CommandDashboardTileSize {
  const { sizes } = COMMAND_DASHBOARD_TILE_SIZE_PROFILES[tile.sizeProfile];
  return (
    sizes.find((size) => size.columns === columns) ?? sizes[sizes.length - 1]
  );
}

export interface CommandDashboardTileDefinition {
  key: CommandDashboardTileKey;
  title: string;
  description: string;
  icon: SvgIconComponent;
  accentColor: CommandDashboardTileAccentColor;
  requiredPermissions: CommandDashboardTilePermission[];
  link?: string;
  sizeProfile: CommandDashboardTileSizeProfile;
}

/** The subset of `useUserSettings()` a tile's permissions are checked against. */
export type CommandDashboardRoles = Record<
  CommandDashboardTileRoleGroup,
  string[]
>;

/** Any one of a tile's required permissions is enough to unlock it. */
export function isTilePermitted(
  tile: CommandDashboardTileDefinition,
  roles: CommandDashboardRoles
): boolean {
  return tile.requiredPermissions.some(({ roleGroup, permission }) =>
    // Fails closed rather than throwing if the role group hasn't hydrated onto
    // userSettings yet (eg. an early render before user settings finish loading).
    isAbleToSeeUIElement(roles[roleGroup] ?? [], permission)
  );
}

/** A human-readable list of the permissions that would unlock a tile, for "you need X" messaging. */
export function describeRequiredPermissions(
  tile: CommandDashboardTileDefinition
): string {
  return tile.requiredPermissions
    .map((permission) => permission.name)
    .join('" or "');
}

/**
 * Payload type every tile drag carries.
 *
 * Nothing reads it back: the grid tracks the dragged tile through the store,
 * because `dataTransfer` payloads are unreadable during `dragover`. It is set
 * because Firefox will not open a drag session for an element made draggable by
 * the attribute unless `dragstart` puts something in the drag data store.
 */
export const TILE_DRAG_MIME_TYPE = 'application/x-gpanel-tile';

/** Marks the header stack as the native-drag handle so tests can find it. */
export const TILE_DRAG_HANDLE_ATTRIBUTE = 'data-tile-drag-handle';

export const COMMAND_DASHBOARD_TILE_LIBRARY: CommandDashboardTileDefinition[] =
  [
    {
      key: 'activeUsers',
      title: 'Active Users',
      description: 'View individuals currently using gPanel.',
      icon: GroupOutlined,
      accentColor: 'blueDark',
      link: pagesConfig.users.link,
      sizeProfile: 'standard',
      requiredPermissions: [
        {
          roleGroup: 'userRoles',
          permission: UserRoles.ViewUserManagement.value,
          name: UserRoles.ViewUserManagement.name,
        },
      ],
    },
    {
      key: 'loginActivity',
      title: 'Activity Logs',
      description: 'Track system events and actions.',
      icon: PendingActionsOutlined,
      accentColor: 'green',
      sizeProfile: 'standard',
      requiredPermissions: [
        {
          roleGroup: 'toolsRoles',
          permission: ToolsRoles.ViewCPanelLogs.value,
          name: ToolsRoles.ViewCPanelLogs.name,
        },
      ],
    },
    {
      key: 'groupsAndAccess',
      title: 'Groups and Access',
      description: 'Manage user groups and permissions.',
      icon: GroupsOutlined,
      accentColor: 'blue',
      sizeProfile: 'standard',
      requiredPermissions: [
        {
          roleGroup: 'directoryRoles',
          permission: DirectoryRoles.ViewGroups.value,
          name: DirectoryRoles.ViewGroups.name,
        },
      ],
    },
    {
      key: 'masterScheduler',
      title: 'Master Scheduler',
      description: 'View and manage scheduled tasks.',
      icon: Event,
      accentColor: 'amber',
      sizeProfile: 'wide',
      requiredPermissions: [
        {
          roleGroup: 'toolsRoles',
          permission: ToolsRoles.ViewReports.value,
          name: ToolsRoles.ViewReports.name,
        },
        {
          roleGroup: 'toolsRoles',
          permission: ToolsRoles.ViewSignatureTemplates.value,
          name: ToolsRoles.ViewSignatureTemplates.name,
        },
        {
          roleGroup: 'toolsRoles',
          permission: ToolsRoles.ViewPolicies.value,
          name: ToolsRoles.ViewPolicies.name,
        },
        {
          roleGroup: 'directoryRoles',
          permission: DirectoryRoles.ViewContactSync.value,
          name: DirectoryRoles.ViewContactSync.name,
        },
      ],
    },
    {
      key: 'recentAdminActions',
      title: 'Recent Admin Actions',
      description: 'Track recent actions by administrators.',
      icon: AdminPanelSettingsOutlined,
      accentColor: 'teal',
      link: pagesConfig.logging.link,
      sizeProfile: 'wide',
      requiredPermissions: [
        {
          roleGroup: 'administrationRoles',
          permission: AdministrationRoles.ViewAppLogs.value,
          name: AdministrationRoles.ViewAppLogs.name,
        },
      ],
    },
    {
      key: 'rulesEngine',
      title: 'Rules Engine',
      description: 'Build complex automations in minutes.',
      icon: Speed,
      accentColor: 'violet',
      link: pagesConfig.rulesEngine.link,
      sizeProfile: 'standard',
      requiredPermissions: [
        {
          roleGroup: 'rulesEngineRoles',
          permission: RulesEngineRoles.ViewRulesGrid.value,
          name: RulesEngineRoles.ViewRulesGrid.name,
        },
      ],
    },
  ];
